import { useState, useCallback, useEffect } from 'react';
import { CORNERS, type Corner } from '../features/assistant/cornerPin';
import type { ProductMapViewMode } from '../features/product-map/productMapRouteSearch';
import type { SandboxesView } from '../features/sandboxes/sandboxesRouteSearch';

const STORAGE_KEY = 'chatsdet_user_settings';

export type FlowsViewMode = 'grouped' | 'table';

export interface FlowsViewSettings {
  /** Which /flows view the user last chose. */
  viewMode: FlowsViewMode;
  /**
   * Collapsed tag sections in the grouped view, keyed per-org (tag ids are
   * org-scoped) then by tag id — with the `'untagged'` sentinel for the
   * neutral section. Sections default to expanded; only explicit collapses
   * are stored.
   */
  collapsedByOrg: Record<string, Record<string, boolean>>;
}

export interface ProductMapViewSettings {
  /** Which /product-map view (List | Explore) the user last chose. */
  viewMode: ProductMapViewMode;
}

export interface SandboxesViewSettings {
  /** The /sandboxes view (v1 | v2) a plain `/sandboxes` opens; saved only by "Set as default". */
  viewMode: SandboxesView;
}

interface UserSettings {
  sidebarCollapsed: boolean;
  docsPanelOpen: boolean;
  assistantPanelOpen: boolean;
  /** Screen corner the assistant launcher puck is pinned to. */
  assistantCorner: Corner;
  flows: FlowsViewSettings;
  productMap: ProductMapViewSettings;
  sandboxes: SandboxesViewSettings;
  // Add more settings here as needed
  // e.g., theme?: 'light' | 'dark';
  // e.g., notificationsEnabled?: boolean;
}

const defaultSettings: UserSettings = {
  sidebarCollapsed: false,
  docsPanelOpen: false,
  assistantPanelOpen: false,
  assistantCorner: 'bottom-right',
  flows: {
    viewMode: 'table',
    collapsedByOrg: {},
  },
  productMap: {
    viewMode: 'list',
  },
  sandboxes: {
    viewMode: 'v1',
  },
};

/**
 * Load settings from localStorage with error handling
 */
function loadSettings(): UserSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...defaultSettings };

    const parsed = JSON.parse(stored);
    // Merge with defaults to ensure new settings have default values.
    // `flows` is nested, so merge it a level deeper too. `assistantCorner`
    // has a closed value set (unlike the plain booleans here) — a
    // corrupted/hand-edited storage value must fall back to the default
    // rather than flow straight into the corner-pinning geometry.
    const assistantCorner = CORNERS.includes(parsed?.assistantCorner)
      ? (parsed.assistantCorner as Corner)
      : defaultSettings.assistantCorner;
    return {
      ...defaultSettings,
      ...parsed,
      assistantCorner,
      flows: { ...defaultSettings.flows, ...parsed.flows },
      productMap: { ...defaultSettings.productMap, ...parsed.productMap },
      sandboxes: { ...defaultSettings.sandboxes, ...parsed.sandboxes },
    };
  } catch (error) {
    console.error('Failed to load user settings from localStorage:', error);
    return { ...defaultSettings };
  }
}

/**
 * Save settings to localStorage with error handling
 */
function saveSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save user settings to localStorage:', error);
  }
}

/**
 * Custom hook for managing persistent user settings
 *
 * Settings are stored in localStorage and automatically synced across the app.
 *
 * @example
 * const { settings, updateSettings } = useUserSettings();
 *
 * // Update a single setting
 * updateSettings({ sidebarCollapsed: true });
 *
 * // Update multiple settings
 * updateSettings({ sidebarCollapsed: true, theme: 'dark' });
 */
export function useUserSettings() {
  const [settings, setSettings] = useState<UserSettings>(loadSettings);

  // Sync to localStorage whenever settings change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  /**
   * Update one or more settings
   * Performs a shallow merge with existing settings
   */
  const updateSettings = useCallback((updates: Partial<UserSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  return {
    settings,
    updateSettings,
  };
}

/**
 * /flows view preferences: persisted view mode + per-org collapsed sections.
 * Thin wrapper over {@link useUserSettings} so FlowList doesn't hand-roll the
 * nested collapsedByOrg bookkeeping.
 */
export function useFlowViewPrefs(orgId: string | null) {
  const { settings, updateSettings } = useUserSettings();
  const flows = settings.flows;

  const setViewMode = useCallback(
    (viewMode: FlowsViewMode) => {
      updateSettings({ flows: { ...flows, viewMode } });
    },
    [flows, updateSettings]
  );

  const isSectionCollapsed = useCallback(
    (sectionKey: string) => {
      if (!orgId) return false;
      return flows.collapsedByOrg[orgId]?.[sectionKey] ?? false;
    },
    [flows.collapsedByOrg, orgId]
  );

  const setSectionCollapsed = useCallback(
    (sectionKey: string, collapsed: boolean) => {
      if (!orgId) return;
      const orgSections = { ...flows.collapsedByOrg[orgId] };
      if (collapsed) {
        orgSections[sectionKey] = true;
      } else {
        // Expanded is the default — drop the key instead of storing false
        delete orgSections[sectionKey];
      }
      updateSettings({
        flows: {
          ...flows,
          collapsedByOrg: { ...flows.collapsedByOrg, [orgId]: orgSections },
        },
      });
    },
    [flows, orgId, updateSettings]
  );

  return {
    viewMode: flows.viewMode,
    setViewMode,
    isSectionCollapsed,
    setSectionCollapsed,
  };
}

/**
 * /product-map view preferences: persisted List | Explore choice. Thin
 * wrapper over {@link useUserSettings}, mirroring {@link useFlowViewPrefs}.
 */
export function useProductMapViewPrefs() {
  const { settings, updateSettings } = useUserSettings();
  const productMap = settings.productMap;

  const setViewMode = useCallback(
    (viewMode: ProductMapViewMode) => {
      updateSettings({ productMap: { ...productMap, viewMode } });
    },
    [productMap, updateSettings]
  );

  return {
    viewMode: productMap.viewMode,
    setViewMode,
  };
}

/**
 * /sandboxes saved default view (v1 | v2). Unlike the other view prefs, the
 * toggle does not write this: only an explicit "Set as default" does.
 */
export function useSandboxesViewPrefs() {
  const { settings, updateSettings } = useUserSettings();
  const sandboxes = settings.sandboxes;

  const setDefaultView = useCallback(
    (viewMode: SandboxesView) => {
      updateSettings({ sandboxes: { ...sandboxes, viewMode } });
    },
    [sandboxes, updateSettings]
  );

  return {
    defaultView: sandboxes.viewMode,
    setDefaultView,
  };
}
